import React, { useState, forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.css';
import './HandsontableDark.css';
import {
    TextField, Box, Alert, Snackbar, Typography, Divider, Tooltip, Button,
} from '@mui/material';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';
import Handsontable from 'handsontable';
import GuidedEntry from './GuidedEntry';
import { apiFetch } from '../api/apiFetch';
import { apiErrorMessage } from '../api/apiError';

const { NumericEditor } = Handsontable.editors;

// Shared input styles matching DataEntryTable.js
const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255, 255, 255, 0.9)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144, 202, 249, 0.7)' },
        '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(144, 202, 249, 0.9)' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)' },
};

/**
 * Generate all Part × Operator × Trial row combinations.
 * Auto-populates the first 3 columns; measurement column starts null.
 * Operator labels: Op1, Op2, Op3 ...
 */
function generateRows(numParts, numOperators, numTrials) {
    const rows = [];
    for (let p = 1; p <= numParts; p++) {
        for (let o = 1; o <= numOperators; o++) {
            for (let t = 1; t <= numTrials; t++) {
                rows.push([p, `Op${o}`, t, null]);
            }
        }
    }
    return rows;
}

/**
 * Build the ordered slot list for GuidedEntry from the current study design.
 * Each slot carries the row index so we can write the value back to the right row.
 * Order: Part 1/Op1/T1 → Part 1/Op1/T2 → ... → Part N/OpM/TK
 * (matches generateRows order: outer loop = Part, middle = Operator, inner = Trial)
 */
function buildGuidedSlots(numParts, numOperators, numTrials) {
    const slots = [];
    for (let p = 1; p <= numParts; p++) {
        for (let o = 1; o <= numOperators; o++) {
            for (let t = 1; t <= numTrials; t++) {
                slots.push({
                    label: `Part ${p} / Op${o} / Trial ${t}`,
                    part: p,
                    operator: `Op${o}`,
                    trial: t,
                    rowIndex: (p - 1) * numOperators * numTrials + (o - 1) * numTrials + (t - 1),
                });
            }
        }
    }
    return slots;
}

/**
 * Extract measurements array from table rows.
 * Only rows where the measurement (col 3) is a finite number are included.
 */
function extractMeasurements(rows) {
    return rows
        .filter(row => row[3] !== null && row[3] !== '' && isFinite(Number(row[3])))
        .map(row => ({
            part:     row[0],
            operator: row[1],
            trial:    row[2],
            value:    Number(row[3]),
            metadata: { timestamp: new Date().toISOString() },
        }));
}

const GRRDataEntryTable = forwardRef(({ onStudyComplete, onError }, ref) => {
    const hotRef = useRef(null);

    // Study configuration
    const [description, setDescription]   = useState('');
    const [numParts, setNumParts]         = useState(10);
    const [numOperators, setNumOperators] = useState(3);
    const [numTrials, setNumTrials]       = useState(2);
    const [tolerance, setTolerance]       = useState('');

    // Table data
    const [tableData, setTableData] = useState(() => generateRows(10, 3, 2));

    // UI state
    const [loading, setLoading]           = useState(false);
    const [error, setError]               = useState(null);
    const [warnings, setWarnings]         = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Guided entry state
    const [guidedMode, setGuidedMode]       = useState(false);
    const [guidedSlots, setGuidedSlots]     = useState([]);
    const [guidedValues, setGuidedValues]   = useState([]);  // parallel to guidedSlots

    // Expose submit + loading + last-submitted measurements to parent
    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
        loading,
        getLastMeasurements: () => extractMeasurements(tableData),
    }));

    // Regenerate rows when study design changes
    const applyDesign = (p, o, t) => {
        const newRows = generateRows(p, o, t);
        setTableData(newRows);
        // Reload into Handsontable instance if it exists
        if (hotRef.current?.hotInstance) {
            hotRef.current.hotInstance.loadData(newRows);
        }
    };

    const handleDesignChange = (field, value) => {
        const val = Math.max(2, Math.min(20, Number(value)));
        if (isNaN(val)) return;
        const p = field === 'parts'     ? val : numParts;
        const o = field === 'operators' ? val : numOperators;
        const t = field === 'trials'    ? val : numTrials;
        if (field === 'parts')     setNumParts(val);
        if (field === 'operators') setNumOperators(val);
        if (field === 'trials')    setNumTrials(val);
        applyDesign(p, o, t);
    };

    const handleHotChange = (changes, source) => {
        if (!changes || source === 'loadData') return;
        const updated = tableData.map(row => [...row]);
        changes.forEach(([row, col, , newValue]) => {
            if (!updated[row]) updated[row] = [null, null, null, null];
            // Only allow editing column 3 (measurement)
            if (col === 3) {
                updated[row][3] = newValue !== '' ? newValue : null;
            }
        });
        setTableData(updated);
    };

    // ---------------------------------------------------------------------------
    // Guided entry handlers
    // ---------------------------------------------------------------------------

    const handleEnterGuided = useCallback(() => {
        const slots = buildGuidedSlots(numParts, numOperators, numTrials);
        // Seed values from current table state (column 3), in slot order
        const seeded = slots.map(slot => {
            const v = tableData[slot.rowIndex]?.[3];
            return (v !== null && v !== undefined && v !== '' && isFinite(Number(v)))
                ? Number(v)
                : null;
        });
        setGuidedSlots(slots);
        setGuidedValues(seeded);
        setGuidedMode(true);
    }, [numParts, numOperators, numTrials, tableData]);

    const handleGuidedSubmitValue = useCallback((val, slotIndex) => {
        setGuidedValues(prev => {
            const next = [...prev];
            next[slotIndex] = val;
            return next;
        });
    }, []);

    const handleExitGuided = useCallback(() => {
        // Write guided values back into table data at the correct row indices
        const updated = tableData.map(row => [...row]);
        guidedSlots.forEach((slot, i) => {
            const v = guidedValues[i];
            if (v !== null && v !== undefined && isFinite(Number(v))) {
                if (!updated[slot.rowIndex]) updated[slot.rowIndex] = [slot.part, slot.operator, slot.trial, null];
                updated[slot.rowIndex][3] = Number(v);
            }
        });
        setTableData(updated);
        // Reload into Handsontable
        if (hotRef.current?.hotInstance) {
            hotRef.current.hotInstance.loadData(updated);
        }
        setGuidedMode(false);
    }, [tableData, guidedSlots, guidedValues]);

    // ---------------------------------------------------------------------------
    // Submit
    // ---------------------------------------------------------------------------

    const handleSubmit = async (event) => {
        if (event) event.preventDefault();
        setLoading(true);
        setError(null);
        setWarnings([]);

        try {
            if (!description.trim()) {
                setError('Please enter a study description.');
                if (onError) onError('Please enter a study description.');
                setLoading(false);
                return;
            }

            const measurements = extractMeasurements(tableData);
            if (measurements.length === 0) {
                setError('Please enter at least some measurements before submitting.');
                if (onError) onError('Please enter at least some measurements before submitting.');
                setLoading(false);
                return;
            }

            const payload = {
                studyType:    'type2',
                description:  description.trim(),
                measurements,
                numParts,
                numOperators,
                numTrials,
                tolerance:    tolerance !== '' ? Number(tolerance) : undefined,
            };

            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const response = await apiFetch(`${backendUrl}/api/msa`, {
                method:      'POST',
                headers:     { 'Content-Type': 'application/json' },
                body:        JSON.stringify(payload),
                credentials: 'include',
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const errMsg = apiErrorMessage(response.status, data, 'Submission failed.');
                setError(errMsg);
                if (onError) onError(errMsg);
                setLoading(false);
                return;
            }

            if (data.warnings?.length > 0) {
                setWarnings(data.warnings);
                setSnackbarOpen(true);
            }

            if (onStudyComplete) onStudyComplete(data);

        } catch (err) {
            console.error('GRR submission error:', err);
            const errMsg = 'Network error — please check your connection and try again.';
            setError(errMsg);
            if (onError) onError(errMsg);
        } finally {
            setLoading(false);
        }
    };

    // ---------------------------------------------------------------------------
    // Render guided entry overlay
    // ---------------------------------------------------------------------------

    if (guidedMode) {
        return (
            <GuidedEntry
                contextLabel={description ? `Type 2 GRR — ${description}` : 'Type 2 GRR — Guided Entry'}
                slots={guidedSlots}
                values={guidedValues}
                onSubmitValue={handleGuidedSubmitValue}
                onClose={handleExitGuided}
                appendMode={false}
                contextChips={tolerance !== '' ? [{ label: 'Tolerance', value: tolerance }] : []}
            />
        );
    }

    const colCount = numParts * numOperators * numTrials;
    const filledCount = tableData.filter(r => r[3] !== null && r[3] !== '').length;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
            {/* Description */}
            <TextField
                label="Study Description"
                variant="outlined"
                size="small"
                fullWidth
                sx={inputSx}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="e.g. Bore diameter gage #4521, Q1 re-study"
                disabled={loading}
                inputProps={{ maxLength: 500 }}
            />

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

            {/* Study design */}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Study Design
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                <TextField
                    label="Parts"
                    type="number"
                    variant="outlined"
                    size="small"
                    sx={inputSx}
                    value={numParts}
                    onChange={e => handleDesignChange('parts', e.target.value)}
                    disabled={loading}
                    inputProps={{ min: 2, max: 20, step: 1 }}
                />
                <TextField
                    label="Operators"
                    type="number"
                    variant="outlined"
                    size="small"
                    sx={inputSx}
                    value={numOperators}
                    onChange={e => handleDesignChange('operators', e.target.value)}
                    disabled={loading}
                    inputProps={{ min: 2, max: 10, step: 1 }}
                />
                <TextField
                    label="Trials"
                    type="number"
                    variant="outlined"
                    size="small"
                    sx={inputSx}
                    value={numTrials}
                    onChange={e => handleDesignChange('trials', e.target.value)}
                    disabled={loading}
                    inputProps={{ min: 2, max: 10, step: 1 }}
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: -0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                    {numParts} parts × {numOperators} operators × {numTrials} trials = {colCount} measurements
                    {filledCount > 0 && ` (${filledCount} entered)`}
                </Typography>
                <Tooltip title="Switch to guided fullscreen entry — walks Part × Operator × Trial in order (keyboard wedge / mobile)">
                    <Button
                        size="small"
                        startIcon={<FullscreenIcon />}
                        onClick={handleEnterGuided}
                        disabled={loading}
                        sx={{ fontSize: '0.7rem', color: 'rgba(144,202,249,0.7)', py: 0.25, px: 1 }}
                    >
                        Guided Entry
                    </Button>
                </Tooltip>
            </Box>

            {/* Tolerance (optional) */}
            <TextField
                label="Tolerance (USL − LSL)"
                variant="outlined"
                size="small"
                fullWidth
                sx={inputSx}
                value={tolerance}
                onChange={e => setTolerance(e.target.value)}
                placeholder="Optional — enables %Tolerance column"
                disabled={loading}
                inputProps={{ inputMode: 'decimal' }}
            />

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

            {/* Helper text */}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                Part, Operator, and Trial columns are auto-populated.
                Enter measurements in the <strong style={{ color: 'rgba(144,202,249,0.8)' }}>Measurement</strong> column only.
            </Typography>

            {/* Error display */}
            {error && (
                <Alert severity="error" sx={{ py: 0.5 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Handsontable */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <HotTable
                    ref={hotRef}
                    data={tableData}
                    colHeaders={['Part', 'Operator', 'Trial', 'Measurement']}
                    rowHeaders={true}
                    licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE || 'non-commercial-and-evaluation'}
                    columns={[
                        { type: 'numeric', readOnly: true,  className: 'htCenter' },
                        { type: 'text',    readOnly: true,  className: 'htCenter' },
                        { type: 'numeric', readOnly: true,  className: 'htCenter' },
                        { type: 'numeric', readOnly: false, editor: NumericEditor, numericFormat: { pattern: '0.00000' }, className: 'htCenter' },
                    ]}
                    afterChange={handleHotChange}
                    afterBeginEditing={(row, col) => {
                        if (col !== 3) return;
                        setTimeout(() => {
                            const el = document.querySelector('.handsontableInput');
                            if (el) {
                                el.setAttribute('inputmode', 'decimal');
                                el.setAttribute('pattern', '[0-9]*[.,]?[0-9]*');
                            }
                        }, 0);
                    }}
                    height="100%"
                    rowHeights={28}
                    manualRowResize={false}
                    contextMenu={true}
                    copyPaste={true}
                    pasteMode="overwrite"
                    allowEmpty={true}
                    allowInvalid={true}
                    autoWrapRow={true}
                    autoWrapCol={true}
                    tabMoves={{ row: 1, col: 0 }}
                    enterMoves={{ row: 1, col: 0 }}
                    selectionMode="multiple"
                    outsideClickDeselects={false}
                    stretchH="all"
                />
            </Box>

            {/* Success warnings snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
                    Study submitted. {warnings.length > 0 && `Warnings: ${warnings.join('; ')}`}
                </Alert>
            </Snackbar>
        </Box>
    );
});

GRRDataEntryTable.displayName = 'GRRDataEntryTable';

export default GRRDataEntryTable;
